import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  Car, MessageSquare, LayoutDashboard, LogOut, Menu, X,
  Plus, ChevronRight, Image, Briefcase
} from 'lucide-react';
import axios from 'axios';
import VehicleForm from '../../components/admin/VehicleForm';
import PromotionForm from '../../components/admin/PromotionForm';
import StatsCards from '../../components/admin/StatsCards';
import { VehiclesTab, ContactsTab, PromotionsTab } from '../../components/admin/DashboardTabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HTTP_UNAUTHORIZED = 401;

const Dashboard = () => {
  const { logout, getToken } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);

  const token = getToken();
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    if (!token) { navigate('/admin/login'); return; }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, vehiclesRes, contactsRes, promosRes] = await Promise.all([
        axios.get(`${API_URL}/api/stats`, { headers }),
        axios.get(`${API_URL}/api/vehicles`, { headers }),
        axios.get(`${API_URL}/api/contacts`, { headers }),
        axios.get(`${API_URL}/api/promotions`, { headers })
      ]);
      setStats(statsRes.data); setVehicles(vehiclesRes.data); setContacts(contactsRes.data); setPromotions(promosRes.data);
    } catch (error) {
      if (error.response?.status === HTTP_UNAUTHORIZED) { logout(); navigate('/admin/login'); }
    } finally { setLoading(false); }
  }, [token, logout, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  // Vehicle handlers
  const handleVehicleSave = async (formData) => {
    try {
      if (editingVehicle) await axios.put(`${API_URL}/api/vehicles/${editingVehicle.id}`, formData, { headers: authHeaders });
      else await axios.post(`${API_URL}/api/vehicles`, formData, { headers: authHeaders });
      setShowVehicleForm(false); setEditingVehicle(null); fetchData();
    } catch (error) { alert(error.response?.data?.detail || "Erreur lors de l'enregistrement du véhicule"); }
  };
  const handleEditVehicle = (vehicle) => { setEditingVehicle(vehicle); setShowVehicleForm(true); };
  const handleDeleteVehicle = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce véhicule ?')) return;
    try { await axios.delete(`${API_URL}/api/vehicles/${id}`, { headers: authHeaders }); fetchData(); } catch (error) { alert('Erreur lors de la suppression'); }
  };
  const handleMarkSold = async (vehicle) => {
    if (!window.confirm(`Marquer "${vehicle.title}" comme vendu ?`)) return;
    try { await axios.put(`${API_URL}/api/vehicles/${vehicle.id}`, { status: 'vendu' }, { headers: authHeaders }); fetchData(); } catch (error) { alert('Erreur lors de la mise à jour'); }
  };
  const handleMarkAvailable = async (vehicle) => {
    try { await axios.put(`${API_URL}/api/vehicles/${vehicle.id}`, { status: 'disponible' }, { headers: authHeaders }); fetchData(); } catch (error) { alert('Erreur lors de la mise à jour'); }
  };

  // Promotion handlers
  const handlePromoSave = async (formData) => {
    try {
      if (editingPromo) await axios.put(`${API_URL}/api/promotions/${editingPromo.id}`, formData, { headers: authHeaders });
      else await axios.post(`${API_URL}/api/promotions`, formData, { headers: authHeaders });
      setShowPromoForm(false); setEditingPromo(null); fetchData();
    } catch (error) { alert(error.response?.data?.detail || "Erreur lors de l'enregistrement de la promotion"); }
  };
  const handleEditPromo = (promo) => { setEditingPromo(promo); setShowPromoForm(true); };
  const handleDeletePromo = async (id) => {
    if (!window.confirm('Supprimer cette promotion ?')) return;
    try { await axios.delete(`${API_URL}/api/promotions/${id}`, { headers: authHeaders }); fetchData(); } catch (error) { alert('Erreur lors de la suppression'); }
  };

  // Contact handlers
  const handleMarkContactRead = async (id) => {
    try { await axios.put(`${API_URL}/api/contacts/${id}/read`, {}, { headers: authHeaders }); fetchData(); } catch (error) { alert('Erreur lors de la mise à jour du message'); }
  };
  const handleDeleteContact = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try { await axios.delete(`${API_URL}/api/contacts/${id}`, { headers: authHeaders }); fetchData(); } catch (error) { alert('Erreur lors de la suppression du message'); }
  };

  const TAB_LABELS = { overview: 'Tableau de bord', vehicles: 'Gestion des véhicules', contacts: 'Messages reçus', promotions: 'Gestion des promotions' };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-100"><div className="spinner"></div></div>;

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center gap-3 p-6 border-b border-gray-200">
            <div className="bg-[#DC2626] text-white font-bold text-lg px-3 py-2 rounded">MCA</div>
            <div className="font-bold text-gray-900">Admin</div>
          </div>
          <nav className="flex-1 p-4 space-y-2">
            {[
              { id: 'overview', icon: LayoutDashboard, label: 'Tableau de bord' },
              { id: 'vehicles', icon: Car, label: 'Véhicules', badge: vehicles.length },
              { id: 'contacts', icon: MessageSquare, label: 'Messages', badge: stats?.unread_contacts, badgeRed: true },
              { id: 'promotions', icon: Image, label: 'Promotions', badge: promotions.length },
            ].map(tab => (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }} className={`sidebar-link w-full text-left ${activeTab === tab.id ? 'active' : ''}`} data-testid={`nav-${tab.id}`}>
                <tab.icon size={20} />{tab.label}
                {tab.badge > 0 && <span className={`ml-auto text-xs px-2 py-1 rounded ${tab.badgeRed ? 'bg-[#DC2626] text-white' : 'bg-gray-200 text-gray-700'}`}>{tab.badge}</span>}
              </button>
            ))}
            <div className="my-3 border-t border-gray-200" />
            <Link to="/admin/crm" className="sidebar-link w-full text-left flex items-center gap-2 bg-[#DC2626]/5 text-[#DC2626] hover:bg-[#DC2626]/10 font-semibold" data-testid="nav-crm">
              <Briefcase size={20} />CRM Garage<ChevronRight size={16} className="ml-auto" />
            </Link>
          </nav>
          <div className="p-4 border-t border-gray-200">
            <button onClick={handleLogout} className="sidebar-link w-full text-left text-red-600 hover:bg-red-50" data-testid="logout-btn"><LogOut size={20} />Déconnexion</button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <div className="lg:ml-64">
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden p-2 hover:bg-gray-100 rounded">{sidebarOpen ? <X size={24} /> : <Menu size={24} />}</button>
            <h1 className="text-xl font-bold text-gray-900">{TAB_LABELS[activeTab]}</h1>
          </div>
          <a href="/" target="_blank" className="text-[#DC2626] hover:underline text-sm flex items-center gap-1">Voir le site<ChevronRight size={16} /></a>
        </header>

        <main className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <StatsCards stats={stats} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-900">Véhicules récents</h2>
                    <button onClick={() => setActiveTab('vehicles')} className="text-[#DC2626] text-sm hover:underline">Voir tous</button>
                  </div>
                  <div className="space-y-3">
                    {vehicles.slice(0, 5).map(v => (
                      <div key={v.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div><p className="font-medium text-gray-900">{v.title}</p><p className="text-sm text-gray-500">{v.price.toLocaleString('fr-FR')} €</p></div>
                        <span className="text-xs px-2 py-0.5 rounded bg-gray-100">{v.status}</span>
                      </div>
                    ))}
                    {vehicles.length === 0 && <p className="text-gray-500 text-center py-4">Aucun véhicule</p>}
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-900">Messages récents</h2>
                    <button onClick={() => setActiveTab('contacts')} className="text-[#DC2626] text-sm hover:underline">Voir tous</button>
                  </div>
                  <div className="space-y-3">
                    {contacts.slice(0, 5).map(c => (
                      <div key={c.id} className="py-2 border-b border-gray-100 last:border-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-gray-900">{c.name}</p>
                          {!c.read && <span className="bg-[#DC2626] text-white text-xs px-2 py-0.5 rounded">Nouveau</span>}
                        </div>
                        <p className="text-sm text-gray-500 truncate">{c.subject}</p>
                      </div>
                    ))}
                    {contacts.length === 0 && <p className="text-gray-500 text-center py-4">Aucun message</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'vehicles' && (
            <VehiclesTab
              vehicles={vehicles}
              onEdit={handleEditVehicle}
              onMarkSold={handleMarkSold}
              onMarkAvailable={handleMarkAvailable}
              onDelete={handleDeleteVehicle}
              onShowForm={() => { setEditingVehicle(null); setShowVehicleForm(true); }}
              VehicleForm={VehicleForm}
              showVehicleForm={showVehicleForm}
              editingVehicle={editingVehicle}
              onVehicleSave={handleVehicleSave}
              onVehicleCancel={() => { setShowVehicleForm(false); setEditingVehicle(null); }}
              authHeaders={authHeaders}
            />
          )}

          {activeTab === 'contacts' && (
            <ContactsTab
              contacts={contacts}
              onMarkRead={handleMarkContactRead}
              onDelete={handleDeleteContact}
            />
          )}

          {activeTab === 'promotions' && (
            <PromotionsTab
              promotions={promotions}
              onEdit={handleEditPromo}
              onDelete={handleDeletePromo}
              onShowForm={() => { setEditingPromo(null); setShowPromoForm(true); }}
              PromotionForm={PromotionForm}
              showPromoForm={showPromoForm}
              editingPromo={editingPromo}
              onPromoSave={handlePromoSave}
              onPromoCancel={() => { setShowPromoForm(false); setEditingPromo(null); }}
              authHeaders={authHeaders}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
